import React, { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react';

/**
 * A textarea that grows with its content up to `maxHeight`, then scrolls
 * inside itself, the way the chat composer (TradeMessages) does. Used by the
 * offer note, purchase note, listing comment and interest reply boxes so
 * they all behave the same (owner, 2026-10-23).
 *
 * Font size is pinned to 16px: iOS Safari zooms the page when a focused
 * field is smaller, and never zooms back out. The app build already forces
 * 16px app-wide (src/index.css, html.capacitor-native); this makes the
 * mobile web match it. Pass any other style; fontSize is the one key that
 * cannot be overridden, on purpose.
 *
 * Height is recomputed whenever `value` changes, not only on typing, so a
 * draft set from outside (the listing page's Reply button prepends an
 * @mention) sizes correctly too.
 */
const AutoGrowTextarea = forwardRef(function AutoGrowTextarea(
  { value, maxHeight = 160, style, rows = 1, ...rest },
  ref
) {
  const innerRef = useRef(null);
  useImperativeHandle(ref, () => innerRef.current);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, maxHeight);
    if (next > 0) el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [value, maxHeight]);

  return (
    <textarea
      ref={innerRef}
      value={value}
      rows={rows}
      {...rest}
      style={{ ...style, fontSize: '16px', resize: 'none', boxSizing: 'border-box', maxHeight: `${maxHeight}px` }}
    />
  );
});

export default AutoGrowTextarea;
